/**
 * Theme Module
 * Handles UI themes (light/dark/nord/sepia) and color palettes for nodes
 */

import { emit, Events } from '../../../engine/core/events.js';
import { getPref, setPref } from '../../../engine/storage/index.js';

// ============================================
// UI Theme (light/dark/nord/sepia)
// ============================================

const UI_THEME_KEY = "project-planner-theme";
const DEFAULT_UI_THEME = "light";

let currentUITheme = DEFAULT_UI_THEME;

/**
 * Get the current UI theme
 * @returns {string} Current theme name
 */
export function getUITheme() {
    return currentUITheme;
}

/**
 * Set the UI theme
 * @param {string} theme - Theme name (light, dark, nord, sepia)
 */
const VALID_UI_THEMES = ["light", "dark", "nord", "sepia"];

export function setUITheme(theme) {
    if (!VALID_UI_THEMES.includes(theme)) {
        console.warn(`Unknown UI theme: ${theme}`);
        return;
    }
    currentUITheme = theme;
    document.documentElement.setAttribute("data-theme", theme);
    setPref(UI_THEME_KEY, theme);
    emit(Events.THEME_CHANGED, { uiTheme: theme });
}

/**
 * Load saved UI theme from localStorage
 */
export function loadUITheme() {
    const saved = getPref(UI_THEME_KEY);
    if (saved && VALID_UI_THEMES.includes(saved)) {
        currentUITheme = saved;
        document.documentElement.setAttribute("data-theme", saved);
    }
    return currentUITheme;
}

// ============================================
// Color Themes (node color palettes)
// ============================================

const COLOR_THEME_KEY = "orbital_color_theme";
const DEFAULT_COLOR_THEME = "vibrant";

/**
 * Color themes with 16 colors each
 */
export const COLOR_THEMES = {
    vibrant: {
        name: "Vibrant",
        colors: [
            "#3b82f6", "#ef4444", "#f59e0b", "#10b981",
            "#8b5cf6", "#ec4899", "#06b6d4", "#6366f1",
            "#84cc16", "#f97316", "#14b8a6", "#a855f7",
            "#e11d48", "#0ea5e9", "#eab308", "#22c55e",
        ],
    },
    pastel: {
        name: "Pastel",
        colors: [
            "#93c5fd", "#fca5a5", "#fcd34d", "#86efac",
            "#c4b5fd", "#f9a8d4", "#67e8f9", "#a5b4fc",
            "#bef264", "#fdba74", "#5eead4", "#d8b4fe",
            "#fda4af", "#7dd3fc", "#fde047", "#4ade80",
        ],
    },
    earth: {
        name: "Earth",
        colors: [
            "#92400e", "#b45309", "#a16207", "#4d7c0f",
            "#166534", "#115e59", "#1e40af", "#5b21b6",
            "#9f1239", "#78350f", "#365314", "#064e3b",
            "#0c4a6e", "#3730a3", "#701a75", "#831843",
        ],
    },
    ocean: {
        name: "Ocean",
        colors: [
            "#0077b6", "#00b4d8", "#0096c7", "#48cae4",
            "#90e0ef", "#00a896", "#02c39a", "#05668d",
            "#028090", "#00509d", "#003566", "#006d77",
            "#0081a7", "#00afb9", "#2a9d8f", "#1d3557",
        ],
    },
    sunset: {
        name: "Sunset",
        colors: [
            "#ff6b6b", "#ee5a24", "#f39c12", "#e74c3c",
            "#d63031", "#e17055", "#fdcb6e", "#f8b739",
            "#ff7675", "#fab1a0", "#fd79a8", "#e84393",
            "#6c5ce7", "#a29bfe", "#ff9ff3", "#feca57",
        ],
    },
    mono: {
        name: "Mono",
        colors: [
            "#1a1a2e", "#16213e", "#0f3460", "#533483",
            "#4a4e69", "#22223b", "#2b2d42", "#3d405b",
            "#6b705c", "#5f6366", "#495057", "#343a40",
            "#212529", "#264653", "#2a4d4a", "#1b4332",
        ],
    },
};

let currentColorTheme = DEFAULT_COLOR_THEME;

/**
 * Get the current color theme name
 * @returns {string} Current color theme name
 */
export function getColorTheme() {
    return currentColorTheme;
}

/**
 * Get the current color palette
 * @returns {string[]} Array of 16 hex colors
 */
export function getCurrentColors() {
    return COLOR_THEMES[currentColorTheme].colors;
}

/**
 * Set the color theme
 * @param {string} themeName - Theme name (vibrant, pastel, earth, ocean, sunset, mono)
 */
export function setColorTheme(themeName) {
    if (!COLOR_THEMES[themeName]) {
        console.warn(`Unknown color theme: ${themeName}`);
        return;
    }
    currentColorTheme = themeName;
    setPref(COLOR_THEME_KEY, themeName);
    emit(Events.COLOR_THEME_CHANGED, { colorTheme: themeName, colors: getCurrentColors() });
}

/**
 * Load saved color theme from localStorage
 */
export function loadColorTheme() {
    const saved = getPref(COLOR_THEME_KEY);
    if (saved && COLOR_THEMES[saved]) {
        currentColorTheme = saved;
    }
    return currentColorTheme;
}

/**
 * Get a color by index from the current palette
 * @param {number} index - Color index (0-15)
 * @returns {string} Hex color
 */
export function getColor(index) {
    const colors = getCurrentColors();
    return colors[index % colors.length];
}

// ============================================
// Special Colors
// ============================================

export const ROOT_COLOR = "#374151"; // Dark grey for root - distinct from child colors

// ============================================
// Initialize
// ============================================

/**
 * Initialize theme system - load saved themes
 */
export function initThemes() {
    loadUITheme();
    loadColorTheme();
}

// ============================================
// UI Helpers for theme dropdown
// ============================================

/**
 * Setup theme dropdown UI
 * @param {HTMLElement} themeBtn - Theme button element
 * @param {HTMLElement} themeDropdown - Dropdown container element
 */
export function setupThemeDropdown(themeBtn, themeDropdown) {
    if (!themeBtn || !themeDropdown) return;

    // Set initial active state
    const currentTheme = getUITheme();
    themeDropdown.querySelectorAll(".theme-option").forEach((opt) => {
        opt.classList.toggle("active", opt.dataset.theme === currentTheme);
    });

    // Toggle dropdown on button click
    themeBtn.onclick = (e) => {
        e.stopPropagation();
        themeDropdown.classList.toggle("active");
    };

    // Handle theme selection
    themeDropdown.querySelectorAll(".theme-option").forEach((opt) => {
        opt.onclick = () => {
            const theme = opt.dataset.theme;
            setUITheme(theme);

            // Update active states
            themeDropdown.querySelectorAll(".theme-option").forEach((o) => {
                o.classList.toggle("active", o.dataset.theme === theme);
            });

            themeDropdown.classList.remove("active");
        };
    });
}
